/**
 * Pointer (mouse + single-finger touch) input capture for the cabinet.
 *
 * Cartridges that want cursor-driven gameplay (DEFENSE GRID's missile
 * aim, TOMB '84's click-to-target, the cabinet's selector-preview epic)
 * read pointer position via `InputState.pointer.{x,y,down}`. The
 * runtime owns the event capture, the coordinate transform from
 * client space → cartridge LOGICAL space, and the held/justPressed
 * edge tracking.
 *
 * Coordinate transform
 * --------------------
 * The visible #game canvas is sized to the bezel × devicePixelRatio
 * (see compositor.ts). Cartridges draw into an offscreen canvas at
 * their declared logical resolution (e.g. 480×270). We get pointer
 * events with `clientX/Y` in CSS pixels of the visible canvas; we
 * need to hand cartridges coords in their logical space.
 *
 * Transform:
 *   1. Subtract canvas getBoundingClientRect() origin (CSS px)
 *   2. Divide by canvas CSS width/height to get [0..1] ratio
 *   3. Multiply by logical resolution (passed in by the launcher,
 *      since it varies per cartridge)
 *
 * Logical resolution comes from setLogicalResolution(w,h) — the
 * launcher invokes that on cartridge boot, so we read it via a
 * setter exported here that the launcher calls in parallel.
 *
 * Lifecycle
 * ---------
 * `attachPointer(canvas)` binds listeners; `detachPointer()` removes
 * them. The launcher calls both around each cartridge run, same
 * pattern as keyboard input and touch controls.
 *
 * No multi-touch
 * --------------
 * Single-pointer only. The first finger to touch the canvas owns
 * the pointer; subsequent fingers are ignored until the first
 * releases. A future story can add multi-touch when a cartridge
 * actually needs it.
 *
 * The "-1 sentinel" rule
 * ----------------------
 * Before any pointer event fires, x/y are -1. Cartridges can use
 * that to render a "MOVE MOUSE TO AIM" hint instead of a stale
 * cursor at (0,0).
 */

import type { PointerState } from '@arcraz/arcadia-types';

const SENTINEL_POSITION = -1;

/** Module-private state. Single pointer source per cabinet — only one
 *  cartridge ever runs at a time, so global state is correct. */
interface PointerModuleState {
  attached: boolean;
  canvas: HTMLCanvasElement | null;
  /** Cartridge logical width — set by the launcher each cartridge boot. */
  logicalW: number;
  /** Cartridge logical height. */
  logicalH: number;
  /** Live position. -1/-1 until first event. */
  x: number;
  y: number;
  /** Live held state. */
  down: boolean;
  /** Held state at the moment of the LAST snapshot — used by the
   *  keyboard input source to derive justPressed/justReleased on
   *  the 'pointer' synthetic button. */
  prevDown: boolean;
}

const state: PointerModuleState = {
  attached: false,
  canvas: null,
  logicalW: 480,
  logicalH: 270,
  x: SENTINEL_POSITION,
  y: SENTINEL_POSITION,
  down: false,
  prevDown: false,
};

/**
 * Set the logical resolution the launcher just configured for the
 * current cartridge. Coordinate transform reads this on every event.
 * Idempotent — safe to call repeatedly with the same values.
 */
export function setPointerLogicalResolution(w: number, h: number): void {
  state.logicalW = w;
  state.logicalH = h;
}

/**
 * Attach pointer listeners to the visible game canvas. Idempotent;
 * second call against the same canvas is a no-op. Detach first if
 * switching canvases.
 */
export function attachPointer(canvas: HTMLCanvasElement): void {
  if (state.attached && state.canvas === canvas) return;
  if (state.attached) detachPointer();

  state.canvas = canvas;
  state.attached = true;
  state.x = SENTINEL_POSITION;
  state.y = SENTINEL_POSITION;
  state.down = false;
  state.prevDown = false;

  canvas.addEventListener('pointerdown', handlePointerDown);
  canvas.addEventListener('pointermove', handlePointerMove);
  canvas.addEventListener('pointerup', handlePointerUp);
  canvas.addEventListener('pointercancel', handlePointerCancel);
  // Tab focus loss — release any held pointer so the cartridge
  // doesn't see a "perma-held" button on return.
  window.addEventListener('blur', handleBlur);
}

export function detachPointer(): void {
  if (!state.attached || state.canvas === null) return;
  state.canvas.removeEventListener('pointerdown', handlePointerDown);
  state.canvas.removeEventListener('pointermove', handlePointerMove);
  state.canvas.removeEventListener('pointerup', handlePointerUp);
  state.canvas.removeEventListener('pointercancel', handlePointerCancel);
  window.removeEventListener('blur', handleBlur);
  state.attached = false;
  state.canvas = null;
}

/**
 * Read the current pointer state for inclusion in this frame's
 * InputState snapshot. Returns a fresh object (cartridges shouldn't
 * see mutations after they save the reference).
 */
export function getPointerSnapshot(): PointerState {
  return {
    x: state.x,
    y: state.y,
    down: state.down,
  };
}

/**
 * Did the pointer transition false→true since the last `markPointerSnapshot()`?
 * The keyboard input source calls this when assembling justPressed.
 */
export function pointerJustPressed(): boolean {
  return state.down && !state.prevDown;
}

/**
 * Did the pointer transition true→false since the last `markPointerSnapshot()`?
 */
export function pointerJustReleased(): boolean {
  return !state.down && state.prevDown;
}

/**
 * Commit the current `down` to `prevDown` so the NEXT frame's
 * justPressed/justReleased diff is correct. Called by the input
 * source at the end of every `snapshot()`.
 */
export function markPointerSnapshot(): void {
  state.prevDown = state.down;
}

// ---- Internal: event handlers ----

function updatePosition(clientX: number, clientY: number): void {
  if (state.canvas === null) return;
  const rect = state.canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;
  const relX = clientX - rect.left;
  const relY = clientY - rect.top;
  const ratioX = Math.max(0, Math.min(1, relX / rect.width));
  const ratioY = Math.max(0, Math.min(1, relY / rect.height));
  // Floor to integer-ish logical coords. Cartridges that want sub-
  // pixel can keep their own running average from history.
  state.x = Math.floor(ratioX * state.logicalW);
  state.y = Math.floor(ratioY * state.logicalH);
}

const handlePointerDown = (e: PointerEvent): void => {
  updatePosition(e.clientX, e.clientY);
  state.down = true;
  // Capture so subsequent move/up events outside the canvas still
  // route here (drag outside the bezel shouldn't drop the input).
  if (state.canvas !== null) {
    try {
      state.canvas.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already
      // released or the browser doesn't support it on this surface.
      // Best-effort.
    }
  }
};

const handlePointerMove = (e: PointerEvent): void => {
  updatePosition(e.clientX, e.clientY);
};

const handlePointerUp = (e: PointerEvent): void => {
  updatePosition(e.clientX, e.clientY);
  state.down = false;
};

const handlePointerCancel = (_e: PointerEvent): void => {
  // Browser canceled the pointer (touch interrupted by a system
  // gesture, etc.). Drop the held state without changing position.
  state.down = false;
};

const handleBlur = (): void => {
  state.down = false;
};
